/**
 * Single source of truth for build-time configuration.
 *
 * Every branch (dev / UAT / Production) ships this file UNCHANGED. Environments
 * differ only in which variables CI supplies -- never in code. Each branch's
 * workflow is the one place environment identity lives, and that file is
 * *supposed* to differ per branch.
 *
 * There is deliberately NO environment-selector variable. Selecting the API host
 * via REACT_APP_ENV / REACT_APP_ENV_PROD / REACT_APP_ENV_UAT was the root cause
 * of the Production -> UAT breakage: the selector's name differed per branch, and
 * UAT and Production had to set the *same* variable to *different* values. The
 * host is now resolved directly from the URL variables, exactly one of which is
 * set in any given build.
 *
 * Canonical names are read first. The legacy per-environment names are a
 * TRANSITION tier so an unchanged dev / Production workflow keeps working after a
 * merge. Delete the legacy tiers once every workflow sets the canonical names.
 */

/**
 * First value that is a non-blank string.
 *
 * GitHub Actions injects an EMPTY STRING for a secret that does not exist, so
 * testing for `undefined` alone is not enough -- an absent secret must fall
 * through to the next candidate.
 */
const firstSet = (...values) =>
  values.find((value) => typeof value === "string" && value.trim() !== "");

/**
 * Base URL for every API call. Consumed by Services/Constants.js (which builds
 * absolute endpoint URLs) and by Services/authService.js (axios baseURL).
 *
 * Intentionally has NO hardcoded fallback host: a misconfigured build must fail
 * loudly (see scripts/check-env.js) rather than silently send traffic to the
 * wrong environment's API.
 */
export const apiUrl = firstSet(
  process.env.REACT_APP_API_BASE_URL, // canonical
  process.env.REACT_APP_PRODUCTION_API_URL, // legacy: Production workflow
  process.env.REACT_APP_STAGING_API_URL, // legacy: UAT workflow
  process.env.REACT_APP_DEV_API_URL, // legacy: dev workflow
  process.env.REACT_APP_LOCAL_API_URL // legacy: local .env
);

/**
 * APIM subscription key sent as the `Ocp-Apim-Subscription-Key` header.
 *
 * Left `undefined` when nothing is set, rather than coerced to "", so axios
 * omits the header entirely -- matching today's behaviour instead of sending an
 * empty header value.
 */
export const apiKey = firstSet(
  process.env.REACT_APP_API_KEY, // canonical (also dev's name)
  process.env.REACT_APP_API_KEY_PROD, // legacy: Production workflow
  process.env.REACT_APP_API_KEY_UAT // legacy: UAT workflow
);

/**
 * Sentry DSN for error monitoring. Optional by design: when unset, Sentry is
 * never initialised and the app runs exactly as before (src/config/sentry.js
 * prints a single warning). Supplied per environment by CI, like the API URL.
 */
export const sentryDsn = firstSet(process.env.REACT_APP_SENTRY_DSN);

/**
 * Human-readable environment label, for diagnostics only.
 *
 * MUST NOT be used to select the API host or any other configuration value --
 * that coupling is the bug this module exists to remove.
 */
export const appEnv =
  firstSet(
    process.env.REACT_APP_ENV,
    process.env.REACT_APP_ENV_PROD,
    process.env.REACT_APP_ENV_UAT
  ) || "unknown";
